/* eslint-disable no-param-reassign */
import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { withStyles } from '~/themes/useStyles'
import { useMedia } from '~/redux/hooks/media'
import { MEDIA_FOLDER_STATIC, PARENT_MODE } from '~/constants/media'
import Tree from '~/components/common/Tree'
import useDrop from '~/utils/hooks/media/useDrop'
import useDragOver from '~/utils/hooks/media/useDragOver'
import { TextBlockToolTip } from '~/components/common/TextBlock'
import style from './style'
import { useUser } from '~/redux/hooks/user'

const getFolderIconSrc = (folderId, folderSelectId, folderDropId) => {
  switch (folderId) {
    case folderSelectId:
      return '_selected'
    case folderDropId:
      return '_drop'
    default:
      return ''
  }
}

const Folder = ({
  classes,
  f,
  dynamicFolder,
  isOpen = false,
  folderDropId,
  onToggle = () => {},
  onEditFolder = () => {},
  onSelectedFolder = () => {},
  folderSelected = {},
  galleryMode,
}) => {
  const dragOverProps = useDragOver(f.id, isOpen, onToggle)
  const dropId =
    f.id === MEDIA_FOLDER_STATIC.ALL_FILE || folderDropId === folderSelected.id
      ? 'noDrop'
      : 'folderDrop'
  const folderIconSrc = getFolderIconSrc(f.id, folderSelected.id, folderDropId)
  const isFolderDropStyle = folderDropId === f.id
  return (
    <div
      data-id={f.id}
      id={dropId}
      className={clsx(
        classes.folderContainer,
        dynamicFolder && 'dynamicFolder',
        isFolderDropStyle && 'dropFolder',
      )}
      {...dragOverProps}
    >
      <div
        className={classes.folderItem}
        onClick={() => onSelectedFolder(f)}
        role="presentation"
        data-id={f.id}
        id={dropId}
      >
        {f.droppable && dynamicFolder && (
          <img
            className={clsx(classes.expandIcon, isOpen && 'isOpen')}
            src="/icons/arrow_right_folder.svg"
            alt="arrow_right_folder"
            data-id={f.id}
            id={dropId}
            onClick={(e) => {
              onToggle()
              e.stopPropagation()
            }}
            role="presentation"
          />
        )}
        <img
          src={`/icons/folder_item${folderIconSrc}.svg`}
          alt="folder_item"
          id={dropId}
          data-id={f.id}
        />
        <TextBlockToolTip
          id={dropId}
          data-id={f.id}
          className={clsx(classes.folderName)}
          title={<span>{f.name}</span>}
        >
          <div
            data-tooltip
            id={dropId}
            data-id={f.id}
            className={clsx(
              classes.folderNameDetail,
              f.id === folderSelected.id && 'selected',
            )}
          >
            {f.name}
          </div>
        </TextBlockToolTip>

        {f.id === folderDropId ? (
          ''
        ) : (
          <span data-id={f.id} id={dropId} className={classes.folderTotal}>
            {f.count}
          </span>
        )}
      </div>
      {dynamicFolder && !galleryMode && (
        <div
          className={classes.editButton}
          role="presentation"
          onClick={() => onEditFolder(f)}
        >
          Edit
        </div>
      )}
    </div>
  )
}

Folder.defaultProps = {
  folderDropId: null,
}

Folder.propTypes = {
  classes: PropTypes.shape().isRequired,
  onSelectedFolder: PropTypes.func.isRequired,
  onEditFolder: PropTypes.func.isRequired,
  dynamicFolder: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  folderDropId: PropTypes.string,
  galleryMode: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  folderSelected: PropTypes.oneOfType([PropTypes.object, PropTypes.shape])
    .isRequired,
  f: PropTypes.oneOfType([PropTypes.object, PropTypes.shape]).isRequired,
}

const Folders = ({
  classes,
  folderSelectedId: mFolderId,
  onSelectedFolder,
  onEditFolder,
  parentMode,
}) => {
  const { mediaFolder, mediaFile, actions } = useMedia()
  const dropRef = useRef()
  const [folderDropId, setFolderDropId] = React.useState('')
  const counter = useRef(0)
  const galleryMode = parentMode.includes(PARENT_MODE.GALLERY)
  const folderSelectedId = useMemo(
    () => mFolderId || MEDIA_FOLDER_STATIC.ALL_FILE,
    [mFolderId],
  )
  const { orgId } = useUser()

  useDrop({
    ref: dropRef,
    onDrop: (event) => {
      if (event.target.id === 'folderDrop') {
        setFolderDropId('')
        const fileSelected = event.dataTransfer
          .getData('fileSelected')
          .split(',')
        const folderId = event.target.getAttribute('data-id')

        if (folderId !== folderSelectedId) {
          actions.moveFileToFolder(
            { parentId: folderId, mediaIdList: fileSelected },
            () => {
              actions.setListFileFolderData({
                total_pages: 0,
                page: 1,
              })
              actions.getListFolder()
              actions.getListFile({
                folder_id: folderSelectedId,
                limit: mediaFile.limit,
                page: 1,
              })
            },
          )
        }
      }
    },
    onDragEnter: (event) => {
      const folderId = event.target.getAttribute('data-id')
      counter.current += 1
      setFolderDropId(folderId)
      event.preventDefault()
    },
    onDragOver: () => {},
    onDragLeave: (event) => {
      const folderId = event.target.getAttribute('data-id')
      if (folderDropId !== folderId) {
        counter.current = 0
      } else {
        counter.current -= 1
      }
      if (counter.current === 0) {
        setFolderDropId('')
      }
    },
  })

  const allFileCount = useMemo(
    () =>
      mediaFolder.list.length
        ? mediaFolder.list.map((e) => e.count).reduce((a, c) => a + c) +
          mediaFolder.unsortedFileCount
        : mediaFolder.unsortedFileCount
          ? mediaFolder.unsortedFileCount
          : 0,
    [mediaFolder.list],
  )

  const folderSelected = useMemo(() => {
    const newFolder = mediaFolder.list.find((f) => f.id === folderSelectedId)
    if (folderSelectedId === MEDIA_FOLDER_STATIC.ALL_FILE) {
      return { id: MEDIA_FOLDER_STATIC.ALL_FILE }
    }

    if (folderSelectedId === MEDIA_FOLDER_STATIC.UNSORTED) {
      return { id: MEDIA_FOLDER_STATIC.UNSORTED }
    }

    return newFolder
  }, [mediaFolder, folderSelectedId])

  useEffect(() => {
    if (orgId) {
      actions.getListFolder()
    }
  }, [orgId])

  const staticFolders = [
    {
      id: MEDIA_FOLDER_STATIC.ALL_FILE,
      name: 'All Files',
      count: allFileCount,
    },
    {
      id: MEDIA_FOLDER_STATIC.UNSORTED,
      name: 'Unsorted Files',
      count: mediaFolder.unsortedFileCount,
    },
  ]

  const renderFolder = (
    f,
    dynamicFolder,
    isOpen = false,
    onToggle = () => {},
  ) => (
    <Folder
      key={f.id}
      classes={classes}
      f={f}
      dynamicFolder={dynamicFolder}
      isOpen={isOpen}
      onToggle={onToggle}
      galleryMode={galleryMode}
      onEditFolder={onEditFolder}
      onSelectedFolder={onSelectedFolder}
      folderSelected={folderSelected}
      folderDropId={folderDropId}
    />
  )
  return (
    <div
      ref={galleryMode ? null : dropRef}
      className={clsx(classes.foldersContainer, 'customized-scrollbar')}
      id="content-folder"
    >
      <span className={classes.title}>Folders</span>
      {staticFolders.map((f) => renderFolder(f, false))}
      <div className={clsx(classes.divider, galleryMode && 'galleryMode')} />
      <Tree
        initialOpenIds={folderSelected?.openIds}
        folders={mediaFolder.list}
        render={(node, { isOpen, onToggle }) =>
          renderFolder(node, true, isOpen, onToggle)
        }
        containerStyles={{ marginLeft: 16 }}
      />
    </div>
  )
}

Folders.defaultProps = {
  parentMode: [],
  folderSelectedId: '',
}
Folders.propTypes = {
  classes: PropTypes.shape().isRequired,
  onSelectedFolder: PropTypes.func.isRequired,
  onEditFolder: PropTypes.func.isRequired,
  parentMode: PropTypes.oneOfType([PropTypes.array, PropTypes.shape]),
  folderSelectedId: PropTypes.string,
}

export default withStyles(style)(Folders)
